/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { View } from '@components/atoms'
import { Footer } from '@components/systems'
import { MonolithAccountNav } from '@components/systems/monolith/MonolithAccountNav'
import { mono, monoFont } from '@components/systems/monolith/monolith.tokens'
import {
	sanitizeReplyHtml,
	downloadBase64File,
	formatFileSize,
} from '@components/organisms/inquiry/replyHtml'
import { customerAccountStyle } from './customerAccount.style'

import customerInquiryApi from '@api/endpoints/home/customerInquiry.api'
import { contactTopicLabel } from '@src/configs/contact.config'
import { useAppSelector } from '@redux/index'
import dayjs from 'dayjs'

/**
 * My Inquiries — Contact 에 남긴 글과 답변 (judahsWeb/INQUIRY_THREAD_PLAN.md §5).
 *
 * 한 페이지에 두 상태: `/customer/inquiry` = 목록(최신 접수가 위), `/customer/inquiry/:id` = 대화 한 건.
 * 대화 주소가 따로 있는 건 답변 메일의 링크가 거기로 오기 때문이다. 새 네비 항목은 아니다.
 * 2026-10-06 이름·주소 변경: My Messages · /customer/messages → My Inquiries · /customer/inquiry
 * ("Messages" 는 주문 알림까지 모이는 받은편지함으로 읽힌다). 옛 주소는 라우트가 넘겨 준다.
 *
 * 배치는 계정 페이지 공용 가운데 760 칼럼. 대화 맨 위 = 돌아가기 · 토픽 · 날짜 · 상태 한 줄.
 * 문의는 이메일로 계정과 잇는다 — 이메일이 인증된 계정만 본다(서버 verifiedCustomerGuard).
 * 대화 = 원문 → 스태프 이메일 답변(보낸 HTML 그대로) → 고객 댓글, 시간순. 내부 메모는 서버가 싣지 않는다.
 */

type CustomerStatus = 'sent' | 'replied' | 'closed'

interface InquiryListItem {
	id: string
	topic: string
	preview: string
	createdAt: string
	status: CustomerStatus
	replyCount: number
	lastActivityAt: string
	hasNewReply: boolean
}

interface ThreadFile {
	id: string
	fileName: string
	contentType: string
	size: number
}

interface ThreadItem {
	id: string
	kind: 'email' | 'customer'
	author: 'you' | 'judahs'
	subject: string
	bodyText: string
	bodyHtml: string
	files: ThreadFile[]
	createdAt: string
}

interface Thread {
	id: string
	topic: string
	message: string
	images: string[]
	createdAt: string
	status: CustomerStatus
	items: ThreadItem[]
}

type LoadStatus = 'loading' | 'ok' | 'empty' | 'notFound' | 'error'

/**
 * 고객에게 보이는 상태 — 누구 차례인지를 말한다.
 *   sent    = 마지막 글이 고객 쪽(처음 보낸 글 · 고객 댓글) → Judahs 답을 기다린다
 *   replied = 마지막 글이 Judahs 답변
 */
const STATUS_LABEL: Record<CustomerStatus, string> = {
	sent: 'Waiting for reply',
	replied: 'Replied',
	closed: 'Closed',
}

/** 서버 MAX_CUSTOMER_COMMENT 와 같다 */
const MAX_REPLY = 2000

const formatDate = (value: string): string => dayjs(value).format('MMM D, YYYY')
const formatDateTime = (value: string): string =>
	dayjs(value).format('MMM D, YYYY · HH:mm')

const MonolithCustomerInquiryPage = () => {
	const { inquiryId } = useParams<{ inquiryId: string }>()
	const verifiedYn: number = useAppSelector(
		state => state.customerAuth.customer.verifiedYn,
	)

	return (
		<View css={style}>
			<View className="mnWrap">
				<MonolithAccountNav />

				<View className="mnAccountBody">
					{verifiedYn !== 1 ? (
						<UnverifiedNotice />
					) : inquiryId ? (
						<ThreadView key={inquiryId} inquiryId={inquiryId} />
					) : (
						<ListView />
					)}
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithCustomerInquiryPage

/** 이메일이 인증되지 않은 계정 — 남의 이메일로 가입해 그 사람의 문의를 읽지 못하게 막는 자리 */
const UnverifiedNotice = () => {
	const navigate = useNavigate()

	return (
		<View className="mnInquiryQuiet">
			<p className="mnAccountQuiet">
				Verify your email in My Info to see your inquiries.
			</p>
			<button
				type="button"
				className="mnTextLink"
				onClick={() => navigate('/customer/info')}
			>
				My Info
			</button>
		</View>
	)
}

const ListView = () => {
	const navigate = useNavigate()
	const [items, setItems] = useState<InquiryListItem[]>([])
	const [hasMore, setHasMore] = useState(false)
	const [status, setStatus] = useState<LoadStatus>('loading')
	const [isLoadingMore, setIsLoadingMore] = useState(false)

	const load = useCallback(async (cursor: string) => {
		const res = await customerInquiryApi.getMyInquiries(cursor)
		const page = res && res.status === 200 ? (res.data as any[])[0] : undefined
		if (!page) return null
		return page as { items: InquiryListItem[]; hasMore: boolean }
	}, [])

	useEffect(() => {
		let isAlive = true
		void load('').then(page => {
			if (!isAlive) return
			if (page === null) {
				setStatus('error')
				return
			}
			setItems(page.items)
			setHasMore(page.hasMore)
			setStatus(page.items.length === 0 ? 'empty' : 'ok')
		})
		return () => {
			isAlive = false
		}
	}, [load])

	const onLoadMore = async () => {
		if (isLoadingMore || items.length === 0) return
		setIsLoadingMore(true)
		const page = await load(items[items.length - 1].id)
		setIsLoadingMore(false)
		if (page === null) return
		setItems(prev => [...prev, ...page.items])
		setHasMore(page.hasMore)
	}

	return (
		<View className="mnInquiryMain">
			{status === 'loading' ? (
				<span className="mnMeta">Loading</span>
			) : status === 'error' ? (
				<p className="mnAccountQuiet">Inquiries could not be loaded. Try again later.</p>
			) : status === 'empty' ? (
				<View className="mnInquiryQuiet">
					<p className="mnAccountQuiet">No inquiries yet.</p>
					<button
						type="button"
						className="mnTextLink"
						onClick={() => navigate('/contact')}
					>
						Contact
					</button>
				</View>
			) : (
				<View className="mnInquiryList">
					{items.map(anItem => (
						<button
							key={anItem.id}
							type="button"
							className="mnInquiryRow"
							onClick={() =>
								navigate(`/customer/inquiry/${encodeURIComponent(anItem.id)}`)
							}
						>
							<span className="mnInquiryRowHead">
								<span className="mnMeta">{contactTopicLabel(anItem.topic)}</span>
								<span className="mnMeta">{formatDate(anItem.createdAt)}</span>
							</span>
							<span className="mnInquiryPreview">{anItem.preview}</span>
							<span className="mnInquiryRowFoot">
								<span className="mnMeta mnInquiryStatus">
									{STATUS_LABEL[anItem.status]}
									{anItem.replyCount > 0 &&
										` · ${anItem.replyCount} ${
											anItem.replyCount === 1 ? 'reply' : 'replies'
										}`}
								</span>
								{anItem.hasNewReply && (
									<span className="mnMeta mnInquiryNew">
										<span className="mnInquiryDot" aria-hidden="true" />
										New
									</span>
								)}
							</span>
						</button>
					))}

					{hasMore && (
						<View className="mnAccountActions mnInquiryMore">
							<button
								type="button"
								className="mnCtaGhost"
								onClick={() => void onLoadMore()}
								disabled={isLoadingMore}
							>
								{isLoadingMore ? 'Loading' : 'Load more'}
							</button>
						</View>
					)}
				</View>
			)}
		</View>
	)
}

const ThreadView = ({ inquiryId }: { inquiryId: string }) => {
	const navigate = useNavigate()
	const [thread, setThread] = useState<Thread | null>(null)
	const [status, setStatus] = useState<LoadStatus>('loading')
	const [reply, setReply] = useState('')
	const [isSending, setIsSending] = useState(false)
	const [sendError, setSendError] = useState('')
	const [isSent, setIsSent] = useState(false)

	useEffect(() => {
		let isAlive = true
		void customerInquiryApi.getMyInquiryThread(inquiryId).then(res => {
			if (!isAlive) return
			const data = res && res.status === 200 ? (res.data as any[])[0] : undefined
			if (data) {
				setThread(data as Thread)
				setStatus('ok')
			} else {
				setStatus(res?.status === 404 ? 'notFound' : 'error')
			}
		})
		return () => {
			isAlive = false
		}
	}, [inquiryId])

	const onSend = useCallback(async () => {
		const body = reply.trim()
		if (!thread || body === '' || isSending) return
		setIsSending(true)
		setSendError('')
		const res = await customerInquiryApi.addMyInquiryComment(thread.id, body)
		setIsSending(false)
		const row =
			res && (res.status === 200 || res.status === 201)
				? (res.data as any[])[0]
				: undefined
		if (row?.item) {
			setThread(prev =>
				prev === null
					? prev
					: {
							...prev,
							status: row.status as CustomerStatus,
							items: [...prev.items, row.item],
					  },
			)
			setReply('')
			setIsSent(true)
			return
		}
		if (res?.status === 409) {
			setThread(prev => (prev === null ? prev : { ...prev, status: 'closed' }))
			return
		}
		setSendError('Your reply could not be sent. Try again later.')
	}, [reply, thread, isSending])

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
			e.preventDefault()
			void onSend()
		}
	}

	const onDownload = async (fileId: string) => {
		if (!thread) return
		const res = await customerInquiryApi.getMyInquiryReplyFile(thread.id, fileId)
		const file = res && res.status === 200 ? (res.data as any[])[0] : undefined
		if (file) downloadBase64File(file)
	}

	const backLink = (
		<button
			type="button"
			className="mnTextLink"
			onClick={() => navigate('/customer/inquiry')}
		>
			← My Inquiries
		</button>
	)

	if (status !== 'ok' || thread === null) {
		return (
			<View className="mnInquiryMain">
				<View className="mnThreadHead">{backLink}</View>
				{status === 'loading' ? (
					<span className="mnMeta">Loading</span>
				) : (
					<p className="mnAccountQuiet">
						{status === 'notFound'
							? 'This inquiry was not found.'
							: 'This inquiry could not be loaded. Try again later.'}
					</p>
				)}
			</View>
		)
	}

	return (
		<View className="mnInquiryMain">
			{/* 맨 위 — 돌아가기, 그리고 이 문의가 무엇인지(토픽 · 접수일 · 누구 차례인지) */}
			<View className="mnThreadHead">
				{backLink}
				<View className="mnThreadFacts">
					<span className="mnKicker mnThreadTopic">
						{contactTopicLabel(thread.topic)}
					</span>
					<span className="mnMeta">
						{formatDate(thread.createdAt)}
						{' · '}
						<span className="mnThreadStatus">{STATUS_LABEL[thread.status]}</span>
					</span>
				</View>
			</View>

			<View className="mnThread">
				<View className="mnThreadEntry mine">
					<span className="mnThreadMeta">
						<span className="mnMeta mnThreadAuthor">You</span>
						<span className="mnMeta">{formatDateTime(thread.createdAt)}</span>
					</span>
					<p className="mnThreadText">{thread.message}</p>
					{thread.images.length > 0 && (
						<View className="mnThreadImages">
							{thread.images.map((aUrl, index) => (
								<a key={aUrl} href={aUrl} target="_blank" rel="noopener noreferrer">
									<img src={aUrl} alt={`Attachment ${index + 1}`} />
								</a>
							))}
						</View>
					)}
				</View>

				{thread.items.map(anItem => (
					<ThreadEntry key={anItem.id} item={anItem} onDownload={onDownload} />
				))}

				{thread.status === 'closed' ? (
					<View className="mnThreadClosed">
						<p className="mnAccountQuiet">
							This inquiry is closed. Send a new message from Contact.
						</p>
						<button
							type="button"
							className="mnTextLink"
							onClick={() => navigate('/contact')}
						>
							Contact
						</button>
					</View>
				) : (
					<View className="mnField mnThreadReply">
						<label className="mnLabel" htmlFor="mnThreadReply">
							Write a reply
						</label>
						<textarea
							id="mnThreadReply"
							className="mnInput mnTextarea"
							value={reply}
							maxLength={MAX_REPLY}
							onChange={e => {
								setReply(e.target.value)
								setIsSent(false)
							}}
							onKeyDown={onKeyDown}
							disabled={isSending}
						/>
						{sendError !== '' && <span className="mnError">{sendError}</span>}
						{isSent && sendError === '' && (
							<span className="mnHint">Your reply has been sent.</span>
						)}
						<View className="mnAccountActions">
							<button
								type="button"
								className="mnCta"
								onClick={() => void onSend()}
								disabled={isSending || reply.trim() === ''}
							>
								{isSending ? 'Sending' : 'Send'}
							</button>
						</View>
					</View>
				)}
			</View>
		</View>
	)
}

const ThreadEntry = ({
	item,
	onDownload,
}: {
	item: ThreadItem
	onDownload: (fileId: string) => Promise<void>
}) => {
	const safeHtml = useMemo(
		() => (item.bodyHtml ? sanitizeReplyHtml(item.bodyHtml) : ''),
		[item.bodyHtml],
	)

	return (
		<View className={`mnThreadEntry ${item.author === 'you' ? 'mine' : 'theirs'}`}>
			<span className="mnThreadMeta">
				<span className="mnMeta mnThreadAuthor">
					{item.author === 'you' ? 'You' : 'Judahs Support'}
				</span>
				<span className="mnMeta">{formatDateTime(item.createdAt)}</span>
			</span>
			{item.subject !== '' && <p className="mnThreadSubject">{item.subject}</p>}
			{safeHtml !== '' ? (
				<div className="mnThreadHtml" dangerouslySetInnerHTML={{ __html: safeHtml }} />
			) : (
				<p className="mnThreadText">{item.bodyText}</p>
			)}
			{item.files.length > 0 && (
				<View className="mnThreadFiles">
					{item.files.map(aFile => (
						<button
							key={aFile.id}
							type="button"
							className="mnThreadFile"
							onClick={() => void onDownload(aFile.id)}
						>
							{aFile.fileName}
							<span className="mnThreadFileSize">{formatFileSize(aFile.size)}</span>
						</button>
					))}
				</View>
			)}
		</View>
	)
}

const style = css({
	...customerAccountStyle,

	'.mnInquiryQuiet': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 20,
	},

	// ── 목록 ──
	'.mnInquiryList': {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
	},

	'.mnInquiryRow': {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		width: '100%',
		padding: '28px 0',
		background: 'transparent',
		border: 0,
		borderTop: `1px solid ${mono.line}`,
		textAlign: 'left',
		fontFamily: monoFont.sans,
		cursor: 'pointer',

		'&:first-of-type': {
			paddingTop: 0,
			borderTop: 0,
		},
		'&:last-of-type': {
			borderBottom: `1px solid ${mono.line}`,
		},
		'&:hover .mnInquiryPreview': {
			color: mono.ink,
		},
		'&:focus-visible': {
			outline: `1px solid ${mono.ink}`,
			outlineOffset: 4,
		},
	},

	'.mnInquiryRowHead, .mnInquiryRowFoot': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
	},

	'.mnInquiryPreview': {
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden',
		fontSize: 16,
		fontWeight: 300,
		lineHeight: 1.6,
		color: mono.dim,
		whiteSpace: 'pre-line',
		wordBreak: 'break-word',
	},

	'.mnInquiryStatus': {
		color: mono.dim,
	},

	'.mnInquiryNew': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
		color: mono.ink,
	},
	'.mnInquiryDot': {
		width: 6,
		height: 6,
		borderRadius: '50%',
		background: mono.ink,
	},

	'.mnInquiryMore': {
		marginTop: 32,
	},

	'.mnInquiryMain': {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
	},

	// ── 대화 ──
	'.mnThreadHead': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 28,
		paddingBottom: 28,
		marginBottom: 28,
		borderBottom: `1px solid ${mono.line}`,
	},
	'.mnThreadFacts': {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: 16,
		flexWrap: 'wrap',
		width: '100%',
	},
	'.mnThreadTopic': {
		color: mono.ink,
	},
	'.mnThreadStatus': {
		color: mono.ink,
	},

	'.mnThread': {
		display: 'flex',
		flexDirection: 'column',
	},

	'.mnThreadEntry': {
		display: 'flex',
		flexDirection: 'column',
		gap: 14,
		padding: '28px 0',
		borderTop: `1px solid ${mono.line}`,

		'&:first-of-type': {
			paddingTop: 0,
			borderTop: 0,
		},
	},
	// 내 글은 왼쪽 선으로 구분한다 — 색 없이
	'.mnThreadEntry.mine': {
		paddingLeft: 20,
		borderLeft: `1px solid ${mono.ink}`,
	},
	'.mnThreadEntry.mine:first-of-type': {
		paddingTop: 4,
	},

	'.mnThreadMeta': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		flexWrap: 'wrap',
	},
	'.mnThreadAuthor': {
		color: mono.ink,
	},

	'.mnThreadSubject': {
		fontSize: 16,
		fontWeight: 500,
		color: mono.ink,
	},

	'.mnThreadText': {
		fontSize: 16,
		fontWeight: 300,
		lineHeight: 1.7,
		color: mono.ink,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},

	// 답변 HTML — 에디터가 만든 태그만 남아 있다(sanitizeReplyHtml)
	'.mnThreadHtml': {
		fontSize: 16,
		fontWeight: 300,
		lineHeight: 1.7,
		color: mono.ink,
		wordBreak: 'break-word',
		p: { margin: '0 0 12px' },
		'p:last-child': { marginBottom: 0 },
		'h1, h2, h3': { fontWeight: 400, margin: '20px 0 10px', lineHeight: 1.3 },
		h1: { fontSize: 24 },
		h2: { fontSize: 20 },
		h3: { fontSize: 18 },
		'ul, ol': { margin: '0 0 12px', paddingLeft: 22 },
		blockquote: {
			margin: '12px 0',
			paddingLeft: 16,
			borderLeft: `1px solid ${mono.lineSoft}`,
			color: mono.dim,
		},
		'pre, code': { fontFamily: 'Menlo, Consolas, monospace', fontSize: 14 },
		pre: { padding: 12, background: mono.bg, overflow: 'auto' },
		hr: { border: 0, borderTop: `1px solid ${mono.line}`, margin: '16px 0' },
		a: { color: mono.ink, textDecoration: 'underline' },
		img: { maxWidth: '100%', height: 'auto' },
		strong: { fontWeight: 500 },
	},

	'.mnThreadImages': {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 10,
		img: {
			display: 'block',
			width: 96,
			height: 96,
			objectFit: 'cover',
			border: `1px solid ${mono.line}`,
		},
	},

	'.mnThreadFiles': {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 10,
	},
	'.mnThreadFile': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 10,
		height: 44,
		padding: '0 14px',
		background: 'transparent',
		border: `1px solid ${mono.lineSoft}`,
		borderRadius: 2,
		fontFamily: monoFont.sans,
		fontSize: 14,
		color: mono.ink,
		cursor: 'pointer',
		transition: 'border-color 150ms',
		'&:hover': { borderColor: mono.ink },
	},
	'.mnThreadFileSize': {
		fontSize: 12,
		color: mono.faint,
	},

	'.mnThreadClosed': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 20,
		paddingTop: 28,
		borderTop: `1px solid ${mono.line}`,
	},

	'.mnThreadReply': {
		paddingTop: 36,
		borderTop: `1px solid ${mono.line}`,
	},

	'.mnInput.mnTextarea': {
		height: 140,
		resize: 'vertical',
		paddingTop: 10,
		lineHeight: 1.6,
	},

	'.mnHint': {
		fontSize: 12,
		color: mono.faint,
	},

	'.mnCta:disabled, .mnCtaGhost:disabled': {
		opacity: 0.4,
		cursor: 'default',
	},
})
